import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, Shield, Users, DollarSign, TrendingUp, FileText,
  CheckCircle2, ArrowRight, Sparkles, Lock, Building2,
} from 'lucide-react';
import Logo from '../components/Logo.js';
import { Button } from '../components/ui/button.jsx';

const Section = ({ children, className = '', id }) => (
  <section id={id} className={`px-6 sm:px-10 py-16 sm:py-24 ${className}`}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const Stat = ({ value, label, sublabel }) => (
  <div className="space-y-1">
    <p className="text-4xl sm:text-5xl font-bold text-white">{value}</p>
    <p className="text-sm font-semibold text-blue-300 uppercase tracking-wider">{label}</p>
    {sublabel && <p className="text-xs text-slate-400">{sublabel}</p>}
  </div>
);

const FeatureCard = ({ icon: Icon, title, children, accent = false }) => (
  <div
    className={`relative rounded-2xl p-6 transition-all hover:-translate-y-1 ${
      accent
        ? 'bg-gradient-to-br from-blue-600 to-blue-800 text-white shadow-lg shadow-blue-900/20'
        : 'bg-white border border-slate-200 hover:border-blue-300 hover:shadow-lg'
    }`}
    data-testid={`feature-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${accent ? 'bg-white/15' : 'bg-blue-50'}`}>
      <Icon className={`w-5 h-5 ${accent ? 'text-white' : 'text-blue-600'}`} />
    </div>
    <h3 className={`text-lg font-semibold mb-2 ${accent ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
    <p className={`text-sm leading-relaxed ${accent ? 'text-blue-100' : 'text-slate-600'}`}>{children}</p>
  </div>
);

const StepCard = ({ n, title, children }) => (
  <div className="relative" data-testid={`step-${n}`}>
    <div className="absolute -left-3 -top-3 w-10 h-10 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center shadow-lg">
      {n}
    </div>
    <div className="pl-8 pt-2">
      <h3 className="text-base font-semibold text-slate-900 mb-1">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
    </div>
  </div>
);

const Differential = ({ title, children }) => (
  <li className="flex items-start gap-3" data-testid={`diff-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
    <div>
      <p className="text-white font-semibold">{title}</p>
      <p className="text-sm text-slate-300">{children}</p>
    </div>
  </li>
);

const LandingPage = () => {
  return (
    <div className="bg-slate-50 min-h-screen" data-testid="landing-page">
      {/* NAV */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-3 flex items-center justify-between">
          <Logo size="default" data-testid="landing-logo" />
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
            <a href="#recursos" className="hover:text-slate-900 transition-colors">Recursos</a>
            <a href="#diferenciais" className="hover:text-slate-900 transition-colors">Diferenciais</a>
            <a href="#como-funciona" className="hover:text-slate-900 transition-colors">Como funciona</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-login-btn">
              <Button variant="outline" className="text-sm">Entrar</Button>
            </Link>
            <Link to="/cadastro" data-testid="landing-cta-cadastro" className="hidden sm:block">
              <Button className="text-sm bg-slate-900 hover:bg-slate-800">Solicitar Acesso</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <Section className="bg-gradient-to-b from-slate-900 via-slate-900 to-blue-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(37,99,235,0.25), transparent 45%), radial-gradient(circle at 80% 60%, rgba(96,165,250,0.15), transparent 50%)'
        }} />
        <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs text-blue-200 mb-6">
              <Sparkles className="w-3 h-3" /> ESG &amp; Workforce Intelligence
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Benchmark institucional<br />
              <span className="text-blue-400">para decisões de RH e ESG</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mb-8">
              Compare sua empresa com o mercado em remuneração, benefícios, indicadores de pessoas e práticas ESG.
              Dados reais, governança estatística rigorosa, sem a sua empresa contaminando a média do mercado.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/cadastro" data-testid="hero-cta-primary">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-12 text-sm">
                  Solicitar Acesso <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-cta-secondary">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 bg-transparent px-6 h-12 text-sm">
                  Já tenho cadastro
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero card mock */}
          <div className="hidden lg:block">
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs uppercase tracking-wider text-blue-300">Índice de Competitividade</p>
                <span className="text-[10px] text-slate-400">n=15 empresas</span>
              </div>
              <p className="text-6xl font-bold text-white mb-2">82</p>
              <p className="text-sm text-emerald-400 font-medium mb-6">Líder de mercado</p>
              <div className="grid grid-cols-3 gap-3 text-xs">
                {[
                  { l: 'Remuneração', v: 78, c: 'bg-blue-500' },
                  { l: 'Benefícios', v: 85, c: 'bg-emerald-500' },
                  { l: 'ESG', v: 84, c: 'bg-violet-500' },
                ].map(b => (
                  <div key={b.l} className="bg-white/5 rounded-lg p-3">
                    <p className="text-slate-400 text-[10px]">{b.l}</p>
                    <p className="text-white font-bold text-lg">{b.v}</p>
                    <div className="h-1 rounded-full bg-white/10 mt-1.5">
                      <div className={`h-full rounded-full ${b.c}`} style={{ width: `${b.v}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mt-16 pt-10 border-t border-white/10 relative">
          <Stat value="15+" label="Empresas" sublabel="Setores diversos" />
          <Stat value="40+" label="Indicadores" sublabel="RH · ESG · Remuneração" />
          <Stat value="n≥5" label="Governança" sublabel="Estatística rigorosa" />
          <Stat value="100%" label="Dados reais" sublabel="Informados pelas empresas" />
        </div>
      </Section>

      {/* RECURSOS */}
      <Section id="recursos">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-2">Recursos</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Tudo o que você precisa para benchmark</h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">Análise institucional consolidada, com filtros dinâmicos por segmento, região e porte.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <FeatureCard icon={DollarSign} title="Pesquisa Salarial" accent>
            Quartis, médias e composição de remuneração (base, HE, adicionais). Compare cargo a cargo com o mercado.
          </FeatureCard>
          <FeatureCard icon={Shield} title="ESG &amp; Pessoas">
            Scores Ambiental, Social, Governança. Diversidade, inclusão, emissões e práticas de equidade.
          </FeatureCard>
          <FeatureCard icon={BarChart3} title="Indicadores de RH">
            Turnover, absenteísmo, headcount, treinamento, satisfação. Sua empresa vs mediana de mercado.
          </FeatureCard>
          <FeatureCard icon={Users} title="Benefícios">
            Cobertura, ticket médio, plano de saúde, vale-refeição. Comparativos por porte e setor.
          </FeatureCard>
          <FeatureCard icon={TrendingUp} title="Acordos Coletivos">
            Tabulação de reajustes, comparação com INPC (IBGE) e projeção temporal.
          </FeatureCard>
          <FeatureCard icon={FileText} title="Relatórios em PDF">
            Exportação institucional de Remuneração e ESG &amp; Pessoas com layout corporativo.
          </FeatureCard>
        </div>
      </Section>

      {/* DIFERENCIAIS */}
      <Section id="diferenciais" className="bg-slate-900 text-white">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-wider text-blue-400 font-semibold mb-2">Diferenciais</p>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-4">Rigor estatístico, dados confiáveis.</h2>
            <p className="text-slate-300 leading-relaxed mb-6">
              Construímos cada análise para que você tenha confiança nas comparações.
            </p>
            <Link to="/cadastro" data-testid="diff-cta">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                Quero participar <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <ul className="space-y-5">
            <Differential title="Amostra mínima n ≥ 5">
              Resultados só são liberados quando há ao menos 5 empresas respondentes — garantia de anonimato e relevância estatística.
            </Differential>
            <Differential title="Empresa excluída da própria média">
              Os números do mercado não incluem a sua empresa, evitando que ela mesma puxe a comparação.
            </Differential>
            <Differential title="Apenas dados reais informados">
              Nada de estimativas ou inferências. Cada indicador vem da resposta de uma empresa real.
            </Differential>
            <Differential title="Governança de cargos">
              Catálogo compartilhado de cargos com nomenclatura padronizada e descrições sumárias.
            </Differential>
            <Differential title="Linha de corte automática">
              ±20% para tirar outliers da média de mercado em comparações salariais cargo a cargo.
            </Differential>
          </ul>
        </div>
      </Section>

      {/* COMO FUNCIONA */}
      <Section id="como-funciona">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-2">Como funciona</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Em 3 passos simples</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-10">
          <StepCard n="1" title="Cadastro institucional">
            Sua empresa solicita acesso ou é convidada pela Consultoria gestora. Dados básicos: razão social, CNPJ, email, telefone.
          </StepCard>
          <StepCard n="2" title="Responde às pesquisas">
            Preencha módulos de RH, ESG, Remuneração e Benefícios. Salvamento progressivo, valide quando estiver pronto.
          </StepCard>
          <StepCard n="3" title="Acessa o benchmark">
            Dashboards interativos, comparativos, índice de competitividade e exportação em PDF.
          </StepCard>
        </div>
      </Section>

      {/* CTA FINAL */}
      <Section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold leading-tight mb-2">
              Pronto para entender onde sua empresa se posiciona no mercado?
            </h2>
            <p className="text-blue-100">Solicite seu acesso agora — ou entre, se já tem cadastro.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Link to="/cadastro" data-testid="bottom-cta-primary">
              <Button className="bg-white text-blue-700 hover:bg-blue-50 px-6 h-11">
                Solicitar Acesso <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login" data-testid="bottom-cta-secondary">
              <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 bg-transparent px-6 h-11">
                Entrar
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-slate-400 px-6 sm:px-10 py-10">
        <div className="max-w-6xl mx-auto grid md:grid-cols-[1.5fr_1fr_1fr_1fr] gap-8">
          <div>
            <Logo size="default" variant="dark" className="text-white mb-3" />
            <p className="text-xs leading-relaxed">
              Plataforma de benchmark ESG &amp; Workforce Intelligence para empresas brasileiras.
              Governança estatística e privacidade da informação.
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-white uppercase tracking-wider mb-3">Plataforma</p>
            <ul className="space-y-2 text-xs">
              <li><a href="#recursos" className="hover:text-white transition-colors">Recursos</a></li>
              <li><a href="#diferenciais" className="hover:text-white transition-colors">Diferenciais</a></li>
              <li><a href="#como-funciona" className="hover:text-white transition-colors">Como funciona</a></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-white uppercase tracking-wider mb-3">Conta</p>
            <ul className="space-y-2 text-xs">
              <li><Link to="/login" className="hover:text-white transition-colors">Entrar</Link></li>
              <li><Link to="/cadastro" className="hover:text-white transition-colors">Cadastro</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-white uppercase tracking-wider mb-3">Legal</p>
            <ul className="space-y-2 text-xs">
              <li className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> LGPD compliant</li>
              <li className="flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Privacidade institucional</li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto pt-8 mt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>© {new Date().getFullYear()} g2statistics · Todos os direitos reservados</p>
          <p>Dados estatísticos com governança n≥5 · Empresa excluída do mercado</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
